<template>
  <div
    v-if="isVisible"
    class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
  >
    <div
      class="bg-white w-full max-w-3xl rounded-2xl shadow-2xl p-8 relative overflow-y-auto max-h-[90vh]"
    >
      <h2 class="text-2xl font-bold text-txt-primary-color mb-6 border-b pb-3">
        Agent Details
      </h2>
      <div class="flex items-center gap-5 mb-6 border-b pb-5">
        <img
          :src="agent?.profilePhoto || defaultPhoto"
          alt="Agent Photo"
          class="w-20 h-20 rounded-full object-cover border-4 border-ds-button-color shadow-md"
        />
        <div>
          <h3 class="text-2xl font-bold text-gray-900">
            {{ agent?.agentname || "N/A" }}
          </h3>
          <p class="text-gray-600 font-medium">Account Officer</p>
          <p class="text-sm text-gray-500">
            Broker:
            <span class="font-semibold text-gray-800">
              {{ agent?.broker_name || "--" }}
            </span>
          </p>
        </div>
      </div>
      <div class="mb-4">
        <button
          @click="showDetails = !showDetails"
          class="flex items-center justify-between w-full bg-white border rounded-lg px-4 py-2 text-sm font-medium shadow-sm hover:bg-gray-50 transition"
        >
          <span>Additional Information</span>
          <svg
            :class="showDetails ? 'rotate-180' : ''"
            class="w-4 h-4 transition-transform"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        <transition name="fade">
          <div
            v-if="showDetails"
            class="mt-2 bg-gray-50 border rounded-lg p-4 text-sm space-y-1"
          >
            <p>
              <span class="font-medium">Agent Code:</span>
              {{ agent?.Agentcode }}
            </p>
            <p>
              <span class="font-medium">Birth Date:</span>
              {{ agent?.birthday }}
            </p>
            <p>
              <span class="font-medium">Email Address:</span>
              {{ agent?.email }}
            </p>
            <p>
              <span class="font-medium">Mobile Number:</span>
              {{ agent?.mobile }}
            </p>
            <p>
              <span class="font-medium">Address:</span>
              {{ agent?.address }}
            </p>
            <p><span class="font-medium">TIN:</span> N/A</p>
            <p><span class="font-medium">SSS:</span> N/A</p>
            <p>
              <span class="font-medium">Emergency Name:</span>
              N/A
            </p>
            <p>
              <span class="font-medium">Emergency Address:</span>
              N/A
            </p>
            <p>
              <span class="font-medium">Emergency Phone:</span>
              N/A
            </p>
          </div>
        </transition>
      </div>
      <div class="grid grid-cols-1 sm:grid-cols-[2fr_3fr] gap-7">
        <div class="space-y-4">
          <div
            class="bg-gray-50 border-l-4 border-ds-button-color rounded-xl p-4 shadow-sm"
          >
            <p class="text-sm text-gray-500 font-medium">Date Added</p>
            <p class="text-base font-semibold text-gray-900">
              {{ formatDate(agent?.DtAdded) }}
            </p>
          </div>
          <div
            class="bg-gray-50 border-l-4 border-ds-button-color rounded-xl p-4 shadow-sm"
          >
            <p class="text-sm text-gray-500 font-medium">Agent Code</p>
            <p class="text-base font-semibold text-gray-900">
              {{ formatAgentCode(agent?.Agentcode) }}
            </p>
          </div>
          <div
            class="bg-gray-50 border-l-4 rounded-xl p-4 shadow-sm flex items-center justify-between"
            :class="isActive ? 'border-green-500' : 'border-rose-500'"
          >
            <div>
              <p class="text-sm text-gray-500 font-medium">Agent Status</p>
              <p
                class="text-base font-semibold"
                :class="isActive ? 'text-green-600' : 'text-rose-500'"
              >
                {{ isActive ? "Active" : "Inactive" }}
              </p>
            </div>
            <label class="relative inline-flex items-center cursor-pointer">
              <input type="checkbox" v-model="isActive" class="sr-only peer" />
              <div
                class="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition"
              ></div>
              <span
                class="absolute left-[2px] top-[2px] w-5 h-5 bg-white rounded-full transition-all peer-checked:translate-x-5"
              ></span>
            </label>
          </div>

          <div
            class="bg-gray-50 border-l-4 rounded-xl p-4 shadow-sm flex items-center justify-between"
            :class="isPrinted ? 'border-green-500' : 'border-rose-500'"
          >
            <div>
              <p class="text-sm text-gray-500 font-medium">ID Card Status</p>
              <p
                class="text-base font-semibold"
                :class="isPrinted ? 'text-green-600' : 'text-rose-500'"
              >
                {{ isPrinted ? "Printed" : "Pending for Printing" }}
              </p>
              <label for="" class="text-sm"
                >Date Printed: {{ formatDate(agent?.dtPrinted) }}</label
              >
            </div>

            <label class="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                :checked="isPrinted"
                @click.prevent="togglePrintingStatus"
                class="sr-only peer"
              />
              <div
                class="w-11 h-6 bg-gray-300 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition"
              ></div>
              <span
                class="absolute left-[2px] top-[2px] w-5 h-5 bg-white rounded-full transition-all peer-checked:translate-x-5"
              ></span>
            </label>
          </div>
        </div>
        <div class="bg-gray-50 p-5 rounded-2xl shadow-inner">
          <h3 class="text-sm text-gray-500 font-medium mb-3">
            List of Companies
          </h3>
          <div class="max-h-80 overflow-y-auto pr-2 custom-scrollbar">
            <ul class="space-y-3 text-sm font-semibold text-gray-800">
              <li
                v-for="(company, index) in formattedCompanies"
                :key="index"
                class="bg-white rounded-lg shadow-sm px-4 py-3 hover:bg-gray-100 transition"
              >
                {{ company }}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="flex justify-end mt-8 gap-3">
        <div>
          <button
            @click="generateAndDownloadQR()"
            class="bg-ds-button-color text-white px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition"
          >
            𖣯 Download QR Code
          </button>
        </div>
        <div>
          <button
            @click="close"
            class="bg-ds-button-color text-white px-6 py-3 rounded-xl font-semibold hover:opacity-90 transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, toRef, onMounted, inject } from "vue";
import axios from "axios";
import { formatAgentCode } from "@/utils/agentcodeFormat";
import { formatDate } from "@/utils/dateFormat";
import QRCode from "qrcode";

export default {
  props: {
    isVisible: { type: Boolean, required: true },
    agent: { type: Object, default: null },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const Swal = inject("$swal");
    const localAgent = toRef(props, "agent");
    const defaultPhoto =
      "https://cdn-icons-png.flaticon.com/512/4128/4128253.png";

    const isActive = ref(true);
    const isPrinted = ref(Number(props.agent.isPrinted) === 1);
    const companies = ref([]);
    const apiMessage = ref("");
    const isLoadingCompanies = ref(false);
    const showDetails = ref(false);

    const close = () => emit("close");

    const toTitleCase = (text) =>
      text
        ?.toLowerCase()
        ?.replace(/\b(\w)/g, (char) => char.toUpperCase())
        ?.replace(/\((.*?)\)/g, (match) => match.toUpperCase());

    const formattedCompanies = computed(() => {
      if (companies.value.length) {
        return companies.value.map((c) => toTitleCase(c));
      }
      return apiMessage.value ? [apiMessage.value] : ["--"];
    });

    const loadCompanies = async () => {
      const agentCode = localAgent.value?.Agentcode;
      if (!agentCode) return;

      isLoadingCompanies.value = true;
      apiMessage.value = "";
      companies.value = [];

      try {
        const response = await axios.get(`/wb/agent/companies/${agentCode}`);

        const data = response.data;

        if (data?.status && data.data?.companies?.length) {
          companies.value = data.data.companies;
        } else {
          apiMessage.value =
            data?.message || "No companies found for this agent.";
        }
      } catch (error) {
        console.error("Error fetching companies:", error);
        apiMessage.value = "Error fetching companies.";
      } finally {
        isLoadingCompanies.value = false;
      }
    };

    onMounted(loadCompanies);

    const togglePrintingStatus = async () => {
      const newValue = !isPrinted.value;
      const agentCode = localAgent.value?.Agentcode;

      if (!agentCode) {
        Swal.fire({
          icon: "error",
          title: "Missing Agent Code",
          text: "Cannot update printing status: agent code is missing.",
        });
        return;
      }

      const { isConfirmed } = await Swal.fire({
        title: "Confirm Action",
        text: newValue
          ? "Mark this ID as Printed?"
          : "Set this ID back to Pending?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#16a34a",
        cancelButtonColor: "#dc2626",
        confirmButtonText: "Yes, continue",
      });

      if (!isConfirmed) return;

      try {
        await axios.put(
          `https://deped-api.onrender.com/wb/agent/printing/${agentCode}`,
          { isPrinted: newValue ? 1 : 0 },
        );

        isPrinted.value = newValue;

        Swal.fire({
          icon: "success",
          title: "Updated",
          text: `ID status set to ${newValue ? "Printed" : "Pending"}.`,
          timer: 1500,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Failed to update printing status:", error);

        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: "Unable to update ID printing status. Please try again.",
        });
      }
    };

    const generateAndDownloadQR = async () => {
      try {
        const publicId = localAgent.value?.public_id;
        if (!publicId) {
          Swal.fire({
            title: "Missing ID",
            text: "This applicant does not have a public ID.",
            icon: "warning",
          });
          return;
        }

        const qrLink = `https://agent.iwcsrvr.com/#/agent/${publicId}`;
        const qrDataUrl = await QRCode.toDataURL(qrLink, {
          width: 300,
          margin: 2,
          color: { dark: "#000000", light: "#ffffff" },
        });

        const link = document.createElement("a");
        link.href = qrDataUrl;
        link.download = `${localAgent.value?.agentname || "applicant"}_QR.png`;
        link.click();
      } catch (err) {
        Swal.fire({
          title: "Error",
          text: "Failed to generate QR code. Please try again.",
          icon: "error",
        });
      }
    };

    return {
      localAgent,
      companies,
      apiMessage,
      isActive,
      isPrinted,
      isLoadingCompanies,
      close,
      loadCompanies,
      toTitleCase,
      formattedCompanies,
      formatAgentCode,
      formatDate,
      defaultPhoto,
      togglePrintingStatus,
      generateAndDownloadQR,
      showDetails,
    };
  },
};
</script>
